'use client';

import { ManifestForm, ManifestSalutation, type ManifestFormProps } from '@modules/manifest';
import { getPersistedPin, setPersistedPin } from '@modules/prism-client/lib/manifest/pin-storage';
import { storeManifestAction } from '@modules/prism-client/lib/manifest/store-manifest-action';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import type { ManifestFieldErrors, ManifestFormValues } from '@modules/manifest';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { Account } from '@tuic/api-kit';
import type { FC } from 'react';
export type ManifestFormBlockProps = ManifestFormProps & {
  traveller: {
    mpi: number;
    pin?: string;
  };
};
const emptyToNull = <T,>(value: T): T | null => typeof value === 'string' && value === '' ? null : value;
const sanitizeSection = <T extends Record<string, unknown>,>(section: T): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(section)) {
    out[key] = emptyToNull(section[key]);
  }
  return out;
};
const reshapeOtherInformation = (values: ManifestFormValues['otherInformation'], salutation: string): Account.ManifestOtherInformation => {
  const {
    mealRequest,
    individualRequest,
    allergy,
    allergyInfo,
    emergencyContactName,
    emergencyContactPhone,
    handicap,
    acceptDataProtectionTerms,
    acceptHealthTerms,
    pregnant24Weeks
  } = values;

  // TBD: backend enforces 'NotPregnant24Weeks must be True'. Non-female
  // travellers don't see the checkbox (see OtherInformationSection), so we
  // hardcode `true` to keep the manifest savable. Remove once the backend
  // accepts `null` or relaxes the rule.
  const isFemale = salutation === ManifestSalutation.FRAU as string;

  // Send health-data consent only when at least one health field (diet or
  // handicap) is filled. If all five are empty, send `null` — even if the
  // user had consented earlier and then cleared the fields.
  const hasHealthData = !!mealRequest || !!individualRequest || allergy.length > 0 || !!allergyInfo || !!handicap;
  return {
    handicap: handicap || null,
    acceptDataProtectionTerms: hasHealthData ? acceptDataProtectionTerms : null,
    acceptHealthTerms,
    pregnant24Weeks: isFemale ? pregnant24Weeks : true,
    dietary: {
      mealRequest: mealRequest || null,
      individualRequest: individualRequest || null,
      allergy: allergy.length > 0 ? allergy : undefined,
      allergyInfo: allergyInfo || null
    },
    emergencyContact: {
      name: emergencyContactName || null,
      phone: emergencyContactPhone || null
    }
  };
};
const reshapeForApi = (values: ManifestFormValues): Account.ManifestDetail => ({
  personalInfo: sanitizeSection(values.personalInfo),
  contact: sanitizeSection(values.contact),
  credentials: sanitizeSection(values.credentials),
  otherInformation: reshapeOtherInformation(values.otherInformation, values.personalInfo.salutation),
  preferredLanguageCode: values.otherInformation.preferredLanguageCode || null
}) as Account.ManifestDetail;
export const ManifestFormBlock: ComponentBlock<ManifestFormBlockProps> = ({
  data,
  fallback
}) => {
  if (fallback) {
    return <ManifestForm fallback />;
  }
  if (!data) return null;
  return <ManifestFormBlockContent {...data} data-sentry-element="ManifestFormBlockContent" data-sentry-component="ManifestFormBlock" data-sentry-source-file="ManifestFormBlock.tsx" />;
};
const ManifestFormBlockContent: FC<ManifestFormBlockProps> = data => {
  const router = useRouter();
  const pathname = usePathname();
  const [isPinRequired, setIsPinRequired] = useState<boolean | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ManifestFieldErrors | null>(null);
  useEffect(() => {
    if (data.traveller?.pin) {
      const persistedPin = getPersistedPin(data.traveller.mpi);
      setIsPinRequired(data.traveller.pin !== persistedPin);
    } else setIsPinRequired(false);
  }, [data.traveller]);
  const handlePinEnter = (enteredPin: string): boolean => {
    if (!data.traveller) return false;
    const pin = data.traveller.pin;
    if (pin !== enteredPin) return false;
    setPersistedPin(data.traveller.mpi, enteredPin);
    setIsPinRequired(false);
    return true;
  };
  const handleSubmit = useCallback(async (formValues: ManifestFormValues) => {
    if (!data.traveller?.mpi) {
      return;
    }
    setFieldErrors(null);
    setIsSubmitting(true);
    const body = reshapeForApi(formValues);
    const result = await storeManifestAction(data.traveller.mpi, body);
    setIsSubmitting(false);
    if (result.isSuccess) {
      // Redirect to the editorial success page. Slug pattern is fixed by
      // the Contentful FlexPage: form sits at `/…/manifest/{id}` and
      // success at `/…/manifest/{id}/success` — appending `/success` to
      // the current pathname covers it. Trim a trailing slash defensively
      // so we don't end up with `…/{id}//success`.
      const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
      const successPath = `${trimmed}/success`;
      router.push(successPath);
    } else if (result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
    }
  }, [data.traveller?.mpi, pathname, router]);
  return <ManifestForm {...data} manifest={data.manifest} fallback={isPinRequired !== false} isPinRequired={isPinRequired} onPinEnter={handlePinEnter} onSubmit={handleSubmit} isSubmitting={isSubmitting} fieldErrors={fieldErrors} data-sentry-element="ManifestForm" data-sentry-component="ManifestFormBlockContent" data-sentry-source-file="ManifestFormBlock.tsx" />;
};